import { storage } from "../../storage/port";
import { showDialog } from "../message/messageSlice";
import { noteClubImportIntent } from "./clubImportPrompt";
import { showClubImportPitchSelector } from "./permissionsSelectors";
/*
  The four "in-content unit" pitches below are defined but not raised anywhere
  yet: the sheet specs them as units that animate into a list, and that
  component does not exist. Their copy is translated and ready — see the
  follow-up ticket for the surfaces that still need building.
*/
export const CLUB_IMPORT_NUDGES = {
    "sbc-solver": { section: "SBC", featureId: "solve-sbc-club" },
    "squad-builder-build": {
        section: "Squad Builder",
        featureId: "build-squad-club",
    },
    "squad-builder-insert": {
        section: "Squad Builder",
        featureId: "see-own-players",
    },
    "evo-builder-overview": {
        section: "Evolutions",
        featureId: "see-own-evo-players",
    },
    "evo-builder-player": { section: "Evolutions", featureId: "evo-chains-club" },
    "evo-players": { section: "Evolutions", featureId: "see-own-evo-players" },
    "players-overview": { section: "Players", featureId: "see-own-players" },
};
/* -------------------------------------------------------------------------- */
/* Cool-off                                                                   */
/* -------------------------------------------------------------------------- */
const COOL_OFF_KEY = "clubImportNudge";
const DAY_MS = 24 * 60 * 60 * 1000;
/** Escalates per dismissal and stays at the last step. */
const DISMISSAL_STEPS_MS = [1 * DAY_MS, 3 * DAY_MS, 7 * DAY_MS];
/** Accepting starts an import; no point asking again while it may be underway. */
const ACCEPTED_MS = 7 * DAY_MS;
const readCoolOff = () => {
    const raw = storage.get(COOL_OFF_KEY);
    if (!raw)
        return null;
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed.until !== "number")
            return null;
        return { until: parsed.until, dismissals: parsed.dismissals ?? 0 };
    }
    catch {
        return null;
    }
};
const writeCoolOff = (coolOff) => storage.set(COOL_OFF_KEY, JSON.stringify(coolOff));
export const isClubImportNudgeCoolingOff = (now = Date.now()) => {
    const coolOff = readCoolOff();
    return coolOff !== null && now < coolOff.until;
};
/** Records how the nudge was left; every exit starts a cool-off. */
export const recordClubImportNudgeExit = (value, now = Date.now()) => {
    const previous = readCoolOff() ?? { until: 0, dismissals: 0 };
    if (value === "positive") {
        writeCoolOff({ ...previous, until: now + ACCEPTED_MS });
        return;
    }
    const dismissals = previous.dismissals + 1;
    const step = DISMISSAL_STEPS_MS[Math.min(dismissals, DISMISSAL_STEPS_MS.length) - 1] ??
        ACCEPTED_MS;
    writeCoolOff({ until: now + step, dismissals });
};
/** Device-level, so deliberately not part of clearCachedEntitlement. */
export const clearClubImportNudgeCoolOff = () => storage.remove(COOL_OFF_KEY);
/* -------------------------------------------------------------------------- */
/* Eligibility and raising                                                    */
/* -------------------------------------------------------------------------- */
/**
 * Whether a nudge may show right now: the user could still do a first import
 * (logged out, or logged in without a club) and no cool-off is running.
 * "loading" counts as no, so a returning importer never sees a flash of it.
 */
export const isClubImportNudgeEligible = (state, now = Date.now()) => showClubImportPitchSelector(state) && !isClubImportNudgeCoolingOff(now);
export const clubImportNudgeOrigin = (nudgeId, subsection) => ({
    section: CLUB_IMPORT_NUDGES[nudgeId].section,
    subsection,
});
/**
 * Raises the nudge dialog. Eligibility is the caller's business (see
 * useClubImportNudge), so this stays a plain action creator like
 * promptClubImport.
 */
export const promptClubImportNudge = (nudgeId, subsection) => showDialog({
    modalType: "club-import-nudge",
    data: {
        nudgeId,
        origin: clubImportNudgeOrigin(nudgeId, subsection),
    },
});
/**
 * The positive exit: records the signup intent (the import that follows ends
 * in a login) and the cool-off. Tracking the CTA click stays with the app,
 * since the two apps have different transports.
 */
export const acceptClubImportNudge = (data) => {
    noteClubImportIntent(data.origin, CLUB_IMPORT_NUDGES[data.nudgeId].featureId);
    recordClubImportNudgeExit("positive");
};
export const dismissClubImportNudge = () => recordClubImportNudgeExit("negative");
//# sourceMappingURL=clubImportNudge.js.map